/**
 * Builds a user facing message out of the `errorMessages` a backend response carries.
 *
 * The generic `fallback` is kept as the first line so the user still knows which
 * operation failed, and every backend reason is listed underneath it. Line breaks
 * render because `.toast-message` is styled with `white-space: pre-line`.
 */
export function formatErrorMessages(fallback: string, errorMessages?: string[] | null): string {
  const messages = (errorMessages || []).map(message => (message || '').trim()).filter(message => !!message);

  if (messages.length === 0) {
    return fallback;
  }

  return [fallback, ...messages.map(message => `• ${message}`)].join('\n');
}

/**
 * Pulls the reason for a failed request out of the error the HTTP layer hands over.
 *
 * The body sits on `error` for a raw `HttpErrorResponse` and on `err` once
 * `ExceptionService` has wrapped it, and backends report the reason either as an
 * `errorMessages` list or as a single message, so all of those shapes are read here.
 */
export function extractErrorMessages(error: unknown): string[] {
  const wrapper = error as { error?: ErrorResponseBody; err?: ErrorResponseBody };
  const body = wrapper?.error ?? wrapper?.err;

  if (!body || typeof body !== 'object') {
    return [];
  }

  const listed = body.errorMessages ?? body.ErrorMessages;

  if (Array.isArray(listed)) {
    return listed.filter((message): message is string => typeof message === 'string');
  }

  const single = body.errorMessage ?? body.message ?? body.Message ?? body.detail;

  return typeof single === 'string' ? [single] : [];
}

interface ErrorResponseBody {
  errorMessages?: unknown;
  ErrorMessages?: unknown;
  errorMessage?: unknown;
  message?: unknown;
  Message?: unknown;
  detail?: unknown;
}
